import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import Paginator from '../../components/Paginator';
import TeamReportListTableHeader from './TeamReportListTableHeader';
import { AccountContext } from '../../components/AccountContext';

const nameTemplate = (rowData, { currentAccount }) => (
  <Link to={`/accounts/${currentAccount}/team-reports/${rowData.id}`} className="app-link">
    {rowData.teamName}
  </Link>
);

const createdTemplate = (rowData, { formatDate }) => {
  const created = formatDate(moment(rowData.createdOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return created;
};

const updatedTemplate = (rowData, { formatDate }) => {
  const updated = formatDate(moment(rowData.updatedOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

const titleTemplate = (rowData) => <p>{rowData.template.title}</p>;

const createdByTemplate = (rowData) => <p>{`${rowData.createdBy.firstName} ${rowData.createdBy.lastName}`}</p>;

const messages = defineMessages({
  teamName: { id: 'team_report.team_name', defaultMessage: 'Team Name' },
  numberOfMembers: { id: 'team_report.number_of_members', defaultMessage: 'Number of members' },
  assessmentTemplate: { id: 'team_report.assessment_template', defaultMessage: 'Assessment Template' },
  createdBy: { id: 'attrs.created_by', defaultMessage: 'Created by' },
  created: { id: 'team_report.created_on', defaultMessage: 'Created on' },
  updated: { id: 'team_report.updated_on', defaultMessage: 'Updated on' },
});

const TeamReportList = ({ connection: { edges, totalCount, indexFrom, indexTo, pageInfo }, loading }) => {
  const currentAccount = useContext(AccountContext);
  const { formatMessage, formatDate } = useIntl();
  const location = useLocation();
  const history = useHistory();

  const onSortChange = (event) => {
    sortUrl(event, location, history);
  };

  const connectionRows = edges.map(({ node }) => ({
    ...node,
  }));

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const teamNameHeader = formatMessage(messages.teamName);
  const numberOfMembersHeader = formatMessage(messages.numberOfMembers);
  const createdHeader = formatMessage(messages.created);
  const updatedHeader = formatMessage(messages.updated);
  const assessmentTemplateHeader = formatMessage(messages.assessmentTemplate);
  const createdByheader = formatMessage(messages.createdBy);

  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <Panel>
              <div className="p-col-12">
                <TeamReportListTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={connectionRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={onSortChange}
                  lazy
                  loading={loading}
                >
                  <Column
                    field="teamName"
                    header={teamNameHeader}
                    body={nameTemplate}
                    currentAccount={currentAccount}
                    sortable
                    style={{ textAlign: 'left' }}
                  />
                  <Column field="numberOfAssessments" header={numberOfMembersHeader} />
                  <Column
                    field="createdOn"
                    header={createdHeader}
                    formatDate={formatDate}
                    body={createdTemplate}
                    sortable
                  />
                  <Column field="createdBy" header={createdByheader} body={createdByTemplate} />
                  <Column
                    field="updatedOn"
                    header={updatedHeader}
                    formatDate={formatDate}
                    body={updatedTemplate}
                    sortable
                  />
                  <Column field="title" header={assessmentTemplateHeader} body={titleTemplate} sortable />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

TeamReportList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default TeamReportList;
